import Button from '@/components/common/Button';
import callAppFunction from '@/utils/bridge';
import { isSayWeeeApp } from '@/utils/weeeapp';
import { XFilled } from '@weee-fe/kunlunicon';
import { Skeleton, useCroppedImage } from '@weee-fe/module';
import router from 'next/router';
import { FC, useEffect, useState } from 'react';
import useClickActionT2 from '@/tracks/useClickActionT2';
import Trans from 'next-translate/Trans';

import BG from './image/annual_bg.png';
import { getLowerMinimumPopup } from '@/api/customer';
import useTranslation from 'next-translate/useTranslation';
import { getDayjsObject } from '@/utils/common/dayjs';

interface Props {
  data?: any;
  visible?: boolean;
  onCancel?: () => void;
}

const BgSvg = () => {
  return (
    <svg width="100%" height="100%" viewBox={`0 0 100% 100%`} xmlns="http://www.w3.org/2000/svg">
      {/* Linear Gradient Background */}
      <defs>
        <linearGradient id="linear-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="3.81%" stop-color="#FFFFFF" />
          <stop offset="28.31%" stop-color="#D2F9DE" />
          <stop offset="59.31%" stop-color="#FFFFFF" />
        </linearGradient>
        <radialGradient id="radial-gradient-1" cx="50%" cy="50%" r="50%">
          <stop offset="25%" stop-color="#FBF6B3" />
          <stop offset="100%" stop-color="rgba(220, 240, 224, 0)" />
        </radialGradient>
        <radialGradient id="radial-gradient-2" cx="50%" cy="50%" r="50%">
          <stop offset="25%" stop-color="#DCF0E0" />
          <stop offset="100%" stop-color="rgba(220, 240, 224, 0)" />
        </radialGradient>
      </defs>
      {/*  Background  */}
      <rect width="100%" height="100%" fill="url(#linear-gradient)" />
      {/*  Radial Gradient Circles  */}
      <circle cx="92%" cy="170" r="243" fill="url(#radial-gradient-2)" />
      <circle cx="-24%" cy="170" r="327" fill="url(#radial-gradient-1)" />
    </svg>
  );
};

const SuccessPage: FC<Props> = (props: Props) => {
  const { onCancel, data } = props;
  const { shipping_info, deadline } = data || {};
  const { getCroppedUrl } = useCroppedImage();
  const { commonClick } = useClickActionT2();
  const { t, lang } = useTranslation('common');

  const handleLearnMore = () => {
    commonClick({
      mod_nm: 'thursday_lower_minimum',
      co: {
        click_type: 'view',
        target_type: 'normal_button',
        target_nm: 'thursday_lower_minimum_landing'
      }
    });
    const link = `/${lang}/promotion/rewards-landing/thursday?fullscreen=1`;
    if (isSayWeeeApp()) {
      callAppFunction('closeCurrentPage', {
        eventType: 'jump_page',
        pageUrl: link
      });
    } else {
      onCancel();
      router.push(link);
    }
  };

  const handleStartShooping = () => {
    commonClick({
      mod_nm: 'thursday_lower_minimum',
      co: {
        click_type: 'view',
        target_type: 'normal_button',
        target_nm: 'start_shopping'
      }
    });
    onCancel();
  };

  return (
    <div id="thursdayLowerMinimum" className="relative">
      <div
        className="absolute z-[2] top-4 right-4 flex items-center justify-center w-9 h-9 rounded-full bg-white"
        style={{
          boxShadow: '0px 5.16px 15.49px 0px #0000001F,0px 0px 3.87px 0px #0000000D'
        }}
        onClick={onCancel}>
        <XFilled className="text-[20px]" />
      </div>
      <div className="absolute inset-0 z-[-1]">
        <BgSvg />
      </div>
      <div
        className="h-[380px] reletive z-[1] px-5 pt-8 pb-5 box-border flex flex-col justify-between"
        style={{
          backgroundImage: `url(${getCroppedUrl(BG, {
            renderWidth: 390,
            devicePixelRatio: 2
          })})`,
          backgroundSize: '100% auto',
          backgroundPosition: 'right bottom',
          backgroundRepeat: 'no-repeat'
        }}>
        <div className="flex-1">
          {!!data && (
            <>
              <div className="mb-3 h-5 inline-flex items-center gap-1 !bg-[#001BA5] rounded-full text-white px-2">
                <span className="enki-body-3xs-medium">{t('rewards_special')}</span>
              </div>
              <p
                className="mb-1 enki-body-base-medium !text-[#001BA5]"
                dangerouslySetInnerHTML={{ __html: t('for_members') }}></p>
              <p className="mb-2 enki-body-2xl-medium !leading-[27px] !text-[#001BA5]">
                <Trans
                  i18nKey="common:thursday_lower_minimum_desc"
                  components={{
                    br: <br />,
                    span: (
                      <span className="inline-block text-secondary-base-3">
                        ${shipping_info?.current_free_shipping}+
                      </span>
                    ),
                    svg: (
                      <span className="inline-block relative w-[44px] text-[17px] align-top text-center">
                        ${shipping_info?.original_free_shipping}
                        <svg
                          className="absolute top-[50%] left-0 -translate-y-1/2"
                          width="44"
                          height="9"
                          viewBox="0 0 44 9"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg">
                          <path
                            d="M1.82499 7.01339C9.58743 4.81629 28.5015 0.737893 42.0584 2.00113"
                            stroke="#001BA5"
                            stroke-width="2"
                            stroke-linecap="round"
                          />
                        </svg>
                      </span>
                    )
                  }}
                />
              </p>
              <p
                className="enki-body-sm-medium text-shade-neutral-dark-1 mb-2 pr-[35%]"
                dangerouslySetInnerHTML={{
                  __html: t('thursday_lower_minimum_desc_2', {
                    current: shipping_info?.current_free_shipping,
                    origin: shipping_info?.original_free_shipping
                  })
                }}
              />
              {/* <p className="enki-body-3xs-medium text-primary-1 pr-[45%]">
                {t('thursday_lower_minimum_tips')}
                <span>
                  <Trans
                    i18nKey="common:thursday_lower_minimum_ends"
                    components={{
                      span: (
                        <>
                          {getDayjsObject(deadline, lang).format(
                            lang === 'ko' ? 'M월 D일' : lang === 'ja' ? 'MMMDD日' : 'M/D'
                          )}
                        </>
                      )
                    }}
                  />
                </span>
              </p> */}
            </>
          )}
        </div>
        <div className="px-2">
          <Button
            onClick={e => {
              e.stopPropagation();
              handleStartShooping();
            }}
            type={'primary'}
            className="!bg-[#001BA5] h-12 !font-semibold">
            {t('start_shopping')}
          </Button>
          <Button
            onClick={e => {
              e.stopPropagation();
              handleLearnMore();
            }}
            className="enki-body-3xs-medium text-shade-neutral-base-7 !bg-inherit">
            {t('check_today_special')}
          </Button>
        </div>
      </div>
    </div>
  );
};

const LoadingSkeleton = () => (
  <div className="h-[380px] px-5 pt-8 pb-5 box-border">
    <div className="mb-3">
      <Skeleton className="h-5 w-28" />
    </div>
    <Skeleton className="h-5 w-40 mb-1" />
    <Skeleton className="h-[54px] w-3/4 mb-2" />
    <Skeleton className="h-10 w-2/3 mb-2" />
    <Skeleton className="h-4 w-1/2" />
    <div className="absolute bottom-8 left-5 right-8">
      <Skeleton className="h-12 w-full !rounded-full mb-2" />
      <Skeleton className="h-8 w-32 mx-auto !rounded-full" />
    </div>
  </div>
);

const ThursdayLowerMinimum = (props: Props) => {
  const { visible, onCancel } = props;
  const [popData, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (visible) {
      const getData = async () => {
        setLoading(true);
        try {
          const res = await getLowerMinimumPopup({ type: 1 });
          if (res?.result) {
            setData(res?.object);
          }
        } finally {
          setLoading(false);
        }
      };
      getData();
    }
  }, [visible]);

  return <div className="relative">{loading ? <LoadingSkeleton /> : <SuccessPage data={popData} {...props} />}</div>;
};

export default ThursdayLowerMinimum;
